import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sparkles, AlertTriangle, CheckCircle, Info, TrendingUp,
  Loader2, RefreshCw, Heart, Shield, Users, AlertCircle,
  Target, Clock, ArrowRight, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const HEALTH_COLORS = {
  excellent: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: CheckCircle },
  good: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Info },
  attention_needed: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertTriangle },
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertCircle }
};

const CATEGORY_ICONS = {
  risk: AlertTriangle,
  opportunity: TrendingUp,
  compliance: Shield,
  relationship: Users
};

const CATEGORY_COLORS = {
  risk: 'bg-red-100 text-red-700',
  opportunity: 'bg-green-100 text-green-700',
  compliance: 'bg-purple-100 text-purple-700',
  relationship: 'bg-blue-100 text-blue-700'
};

function MetricBar({ label, value, color = 'default' }) {
  const colors = {
    default: 'bg-[#4A3B32]',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500'
  };
  
  const barColor = value >= 70 ? colors.success : value >= 40 ? colors.warning : colors.danger;
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[#5D524D]">{label}</span>
        <span className="font-medium text-[#2C2420]">{value}%</span>
      </div>
      <div className="h-2 bg-[#E6E0D9] rounded-full overflow-hidden">
        <div 
          className={cn("h-full rounded-full transition-all", barColor)}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

export default function AIClientInsights({ clientId, clientName }) {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [insightsData, setInsightsData] = useState(null);
  const [showAllInsights, setShowAllInsights] = useState(false);

  useEffect(() => {
    // Load cached insights on mount
    loadCachedInsights();
  }, [clientId]);

  const loadCachedInsights = async () => {
    try {
      const response = await api.get(token, `/ai/client-insights/${clientId}`);
      if (response.data.hasInsights) {
        setInsightsData(response.data);
      }
    } catch (error) {
      // No cached insights - that's okay
    }
  };

  const generateInsights = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(token, '/ai/client-insights', { clientId });
      setInsightsData({ ...response.data, hasInsights: true });
      toast.success('Insights generated successfully');
    } catch (error) {
      console.error('Insights error:', error);
      toast.error('Failed to generate insights');
    }
    setIsLoading(false);
  };

  const health = insightsData?.overallHealth;
  const healthColors = health ? HEALTH_COLORS[health] : null;

  // If no insights yet
  if (!insightsData?.hasInsights) {
    return (
      <div className="bg-gradient-to-br from-[#F9F7F5] to-white rounded-xl border border-[#E6E0D9] p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#4A3B32]/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#4A3B32]" />
          </div>
          <div>
            <h4 className="font-medium text-[#2C2420]">AI Client Insights</h4>
            <p className="text-xs text-[#5D524D]">Powered by GPT-5.2</p>
          </div>
        </div>
        
        <p className="text-sm text-[#5D524D] mb-4">
          Get AI-powered insights about {clientName || 'this client'}, including risk indicators, 
          opportunities, and recommended actions.
        </p>
        
        <Button
          onClick={generateInsights}
          disabled={isLoading}
          className="w-full bg-[#4A3B32] hover:bg-[#3A2E26]"
          data-testid="generate-ai-insights-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing Client Data...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate AI Insights
            </>
          )}
        </Button>
      </div>
    );
  }

  const HealthIcon = healthColors?.icon || Info;
  const displayedInsights = showAllInsights 
    ? insightsData.insights 
    : insightsData.insights?.slice(0, 3);

  return (
    <div className={cn(
      "rounded-xl border p-5 transition-all",
      healthColors?.bg || 'bg-white',
      healthColors?.border || 'border-[#E6E0D9]'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center",
            healthColors?.bg || 'bg-[#4A3B32]/10'
          )}>
            <HealthIcon className={cn("w-5 h-5", healthColors?.text || 'text-[#4A3B32]')} />
          </div>
          <div>
            <h4 className="font-medium text-[#2C2420]">AI Client Insights</h4>
            <span className={cn(
              "text-xs font-medium px-2 py-0.5 rounded-full",
              healthColors?.bg, healthColors?.text
            )}>
              {health?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={generateInsights}
          disabled={isLoading}
          className="h-8 px-2"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Summary */}
      {insightsData.summary && (
        <p className="text-sm text-[#2C2420] mb-4 leading-relaxed">
          {insightsData.summary}
        </p>
      )}

      {/* Key Metrics */}
      {insightsData.keyMetrics && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <MetricBar 
            label="Data" 
            value={insightsData.keyMetrics.dataCompleteness || 0} 
          />
          <MetricBar 
            label="Compliance" 
            value={insightsData.keyMetrics.complianceScore || 0} 
          />
          <MetricBar 
            label="Engagement" 
            value={insightsData.keyMetrics.engagementScore || 0} 
          />
        </div>
      )}

      {/* Insights List */}
      {displayedInsights && displayedInsights.length > 0 && (
        <div className="space-y-3 mb-3">
          <h5 className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">
            Key Insights
          </h5>
          {displayedInsights.map((insight, idx) => {
            const CategoryIcon = CATEGORY_ICONS[insight.category] || Info;
            return (
              <div 
                key={idx}
                className="bg-white/80 rounded-lg p-3 border border-[#E6E0D9]/50"
              >
                <div className="flex items-start gap-2">
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
                    CATEGORY_COLORS[insight.category] || 'bg-gray-100 text-gray-700'
                  )}>
                    <CategoryIcon className="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h6 className="font-medium text-sm text-[#2C2420]">{insight.title}</h6>
                      <span className={cn(
                        "text-xs px-1.5 py-0.5 rounded",
                        insight.priority === 'high' ? 'bg-red-100 text-red-700' :
                        insight.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      )}>
                        {insight.priority}
                      </span>
                    </div>
                    <p className="text-xs text-[#5D524D] mb-1">{insight.description}</p>
                    {insight.action && (
                      <p className="text-xs text-[#4A3B32] flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        {insight.action}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Show More */}
      {insightsData.insights && insightsData.insights.length > 3 && (
        <button
          onClick={() => setShowAllInsights(!showAllInsights)}
          className="w-full text-center text-sm text-[#4A3B32] hover:underline flex items-center justify-center gap-1"
        >
          {showAllInsights ? 'Show Less' : `Show All ${insightsData.insights.length} Insights`}
          <ArrowRight className={cn("w-3 h-3 transition-transform", showAllInsights && "rotate-90")} />
        </button>
      )}

      {/* Generated At */}
      {insightsData.generatedAt && (
        <p className="text-xs text-[#8C8078] text-center mt-3 pt-3 border-t border-[#E6E0D9]/50">
          <Clock className="w-3 h-3 inline mr-1" />
          Generated {new Date(insightsData.generatedAt).toLocaleDateString('en-AU', {
            day: 'numeric', month: 'short', year: 'numeric'
          })}
        </p>
      )}
    </div>
  );
}

// Compact Health Badge
export function AIClientHealthBadge({ health, onClick }) {
  if (!health) return null;
  
  const colors = HEALTH_COLORS[health];
  const Icon = colors?.icon || Info;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all hover:scale-105",
        colors?.bg, colors?.text
      )}
      title={`AI Health: ${health.replace('_', ' ')}`}
    >
      <Icon className="w-3 h-3" />
      {health.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </button>
  );
}
